import { useParams, useNavigate, generatePath } from "react-router-dom";
import { ProjectRoleForm } from "@/modules/projects/components/roles/ProjectRoleForm";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import {
  Module,
  PATH_PROJECT_ROLES,
  PATH_PROJECTS,
  PATH_ROOT,
} from "@/constants";
import { useMemo } from "react";
import { BreadcrumbItem } from "@/types/breadcrumb";
import { generateDisplayName } from "@/modules/projects/lib/generateDisplayName";
import { ReadProjectRole, WriteProjectRole } from "@/types/projectRoleType";
import { ProjectRolesService } from "@/modules/projects/service/projectRole";
import { ApiError } from "@/services/api";
import { toast } from "@/components/ui/use-toast";
import { exists } from "i18next";
import { UserPlus } from "lucide-react";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";

export const ProjectRoleCreator = () => {
  const { projectId } = useParams() as {
    projectId: string;
  };
  const { t } = useTranslation();
  const title = t("projectRoles.title.add");
  const navigate = useNavigate();
  const { can_create } = useModulePermissions();
  const projectQuery = useProjectQuery(projectId);
  const isProjectLocked = useCheckIsProjectLocked(projectId);

  const { invalidateListQuery } = useQueryCacheUpdater(Module.ProjectRoles);

  const breadcrumbItems = useMemo(() => {
    const breadcrumbItems: BreadcrumbItem[] = [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("projects.breadcrumb.projects"), href: PATH_PROJECTS },
    ];

    breadcrumbItems.push({
      label: t("projects.breadcrumb.project", {
        name:
          projectQuery.data !== undefined
            ? generateDisplayName(projectQuery.data)
            : "...",
      }),
      href: `${PATH_PROJECTS}/${projectId}`,
    });

    breadcrumbItems.push({
      label: t(`projects.breadcrumb.roles`),
      href: generatePath(PATH_PROJECT_ROLES, { projectId }),
    });

    breadcrumbItems.push({
      label: title,
    });
    return breadcrumbItems;
  }, [t, projectId, projectQuery.data, title]);

  const createProjectRoleMutationFn: MutationFunction<
    ReadProjectRole,
    WriteProjectRole
  > = async (values) => ProjectRolesService.post(projectId, values);

  const createProjectRoleMutation = useMutation<
    ReadProjectRole,
    ApiError,
    WriteProjectRole
  >({
    mutationFn: createProjectRoleMutationFn,
    onSuccess: () => {
      toast({
        title: t("projectRoles.success.create"),
      });
      invalidateListQuery();
      navigate(`${PATH_PROJECTS}/${projectId}/${Module.ProjectRoles}`);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("projectRoles.errors.create"),
        description,
        variant: "destructive",
      });
    },
  });

  const handleCreateProjectRole = (formData: WriteProjectRole) => {
    createProjectRoleMutation.mutate(formData);
  };

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <ProjectDetailsAndNavigation />
      <ProjectRoleForm
        onSubmit={handleCreateProjectRole}
        disabled={
          createProjectRoleMutation.isPending || !can_create || isProjectLocked
        }
        icon={<UserPlus className="w-6 h-6 mr-3" />}
        title={title}
      />
    </>
  );
};
